import React, { FC, useCallback, useEffect, useState } from 'react'
import styles from 'styles/components/template.module.scss'
import { Description, SmallHeadline } from 'components/Typography'
import { Spacer } from 'components/Utility'
import { BlueButton, NavButton } from 'components/Button'
import { SearchForm, SelectBox } from 'components/Form'
import { CautionCard, MenuCard, ScheduleCardList } from 'components/Card'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'index'
import { getBreads } from 'reducks/bread/selectors'
import { getStoreRef } from 'reducks/auth/selectors'
import { BreadType } from 'types/bread'
import { push } from 'connected-react-router'
import { filterBreadsByCategory, filterBreadsByKeyword } from 'lib/filter'
import { fetchBreads } from 'reducks/bread/operations'
import { useCategoryOptions, useFetchCategories } from 'hooks/category'
import { useHandleKeyword, useSearchBreads, useSelectCategoryName } from 'hooks/storeDeliveryBread'

const type = 'delivery'
const sortKey = 'order'

const DeliveryScheduleTemplate: FC = () => {
  const dispatch = useDispatch()
  const breads = useSelector((state: State) => getBreads(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const categories = useFetchCategories()

  const [categoryName, setCategoryName] = useState<string>('')
  const [keyword, setKeyword] = useState<string>('')
  const [breadList, setBreadList] = useState<BreadType[]>([])

  const goToEditCollection = useCallback(() => {
    dispatch(push('/schedule/delivery/collective_setting'))
  }, [])

  // 選択されたカテゴリーに応じてフィルタリング
  const selectCategoryName = useSelectCategoryName(breads, categories, keyword, setCategoryName, setBreadList)
  // 検索キーワードの入力
  const handleKeyword = useHandleKeyword(breads, categories, categoryName, setKeyword, setBreadList)
  // キーワード検索(部分一致)
  const searchBreads = useSearchBreads(breads, categories, categoryName, keyword, setCategoryName, setBreadList)

  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchBreads(storeRef, 'public', sortKey, type))
  }, [storeRef, sortKey])

  // ReduxのStoreにあるbreadsをローカルのbreadListに反映する
  useEffect(() => {
    let _breads = breads
    // カテゴリが指定されていればフィルタリング
    if (categoryName !== '') {
      _breads = filterBreadsByCategory(categoryName, categories, breads)
    }
    // 検索キーワードが入力されていればフィルタリング
    if (keyword !== '') {
      _breads = filterBreadsByKeyword(keyword, _breads)
    }
    setBreadList([..._breads])
  }, [breads])

  // カテゴリの選択肢を生成
  const categoryOptions = useCategoryOptions(categories)

  return (
    <div className={styles.standard}>
      <Spacer size={28} />
      <Description description={'取り寄せ商品のお届け可能日を設定したり、1日あたりの販売上限数を変更できます。'} />
      <Spacer size={20} />
      <section>
        <Spacer size={20} />
        <SearchForm
          isFilled={keyword !== ''}
          onChange={handleKeyword}
          onClick={searchBreads}
          placeholder={'商品名で検索する'}
          value={keyword}
        />
        <Spacer size={12} />
        <SelectBox options={categoryOptions} select={selectCategoryName} value={categoryName} />
        <Spacer size={28} />
        <SmallHeadline label={`${breadList.length}件の商品を表示中`} />
        <Spacer size={12} />
        {breads.length === 0 ? (
          <>
            <CautionCard
              align={'center'}
              caution={'設定できる商品がありません'}
              description={
                'お届け可能日を設定できる商品が登録されておりません。sacriで出品する商品の管理から、取り寄せを受け付ける商品を登録してください。'
              }
            />
            <Spacer size={20} />
            <MenuCard isPrimary={true} label={'sacriで出品する商品の管理'} url={'/product'} />
          </>
        ) : // キーワードやカテゴリでのフィルタリング結果が0件の場合
        breadList.length === 0 ? (
          <CautionCard
            align={'center'}
            caution={'該当商品がありません'}
            description={'別の方法で検索してみてください。'}
          />
        ) : (
          // 事前予約設定済みの商品が存在する場合
          <>
            <ScheduleCardList breads={breadList} type={type} />
            <Spacer size={48} />
            <BlueButton
              label={'お届け可能日を一括設定する'}
              onClick={goToEditCollection}
              size={'medium'}
              type={'main'}
            />
          </>
        )}
      </section>
      <Spacer size={48} />
      <NavButton label={'トップページに戻る'} iconPath={'/icon/grey/home.svg'} url={'/'} />
      <Spacer size={72} />
    </div>
  )
}

export default DeliveryScheduleTemplate
